import { Injectable } from '@angular/core';
import { Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private _authService: AuthService,
    private _router: Router
  ) {}

  //==========================================================
  //? @PUBLIC METHODS
  //==========================================================
  canMatch(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this._check(segments);
  }

  //==========================================================
  //? @PRIVATE METHODS
  //==========================================================

  /**
   * Check the authenticated status
   *
   * @param segments
   * @private
   */
  private _check(segments: UrlSegment[]): Observable<boolean | UrlTree> {
    // Check the authentication status
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        // If the user is not authenticated...
        if (!authenticated) {
          const urlTree = this._router.createUrlTree(['/auth/sign-in'], {
            queryParams: { redirectURL: this._redirectUrl(segments) },
          });

          return of(urlTree);
        } else if (!sessionStorage.getItem('primecontrol_idInstance')) {
          // The instance lives in sessionStorage, so every new tab lands here —
          // including one opened from an email link. Carry the link along so the
          // selector enters it instead of /home.
          const urlTree = this._router.createUrlTree(['/select-instance'], {
            queryParams: { redirectURL: this._redirectUrl(segments) },
          });

          return of(urlTree);
        }

        // Allow the access
        return of(true);
      })
    );
  }

  /**
   * The URL being navigated to, path and query string, so things like
   * ?invitation_token=... survive the redirect.
   *
   * @param segments The matched URL segments
   * @private
   */
  private _redirectUrl(segments: UrlSegment[]): string {
    const path = `/${segments.join('/')}`;
    const queryParams = this._router.getCurrentNavigation()?.extractedUrl?.queryParams ?? {};
    const search = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value != null) search.set(key, String(value));
    });
    const queryString = search.toString();
    return queryString ? `${path}?${queryString}` : path;
  }
}
